import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { authApi } from '../services/api';
import { CheckCircle2 } from 'lucide-react';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/account'), 2500);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="py-16">
      <div className="container-max max-w-md mx-auto">
        <h1 className="text-3xl font-serif font-bold text-coastal-600 mb-2 text-center">
          Reset Password
        </h1>
        <p className="text-gray-600 text-center mb-8">
          Choose a new password for your account.
        </p>

        <Card>
          <CardContent className="pt-6">
            {done ? (
              <div className="text-center space-y-4">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
                <p className="text-gray-700">
                  Your password has been updated. Redirecting you to sign in...
                </p>
                <Link to="/account" className="text-coastal-600 font-medium hover:underline inline-block">
                  Sign in now
                </Link>
              </div>
            ) : !token ? (
              <div className="text-center space-y-4">
                <p className="text-red-600">This reset link is missing its token.</p>
                <Link to="/forgot-password" className="text-coastal-600 font-medium hover:underline inline-block">
                  Request a new link
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    minLength={8}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    minLength={8}
                    required
                  />
                </div>
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? 'Please wait...' : 'Update password'}
                </Button>
                {error && (
                  <div className="text-center text-sm text-gray-600">
                    <Link to="/forgot-password" className="text-coastal-600 font-medium hover:underline">
                      Request a new link
                    </Link>
                  </div>
                )}
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
